import type { NavigateFunction } from "react-router-dom";
import { resolveTopAuthorSlug } from "../constants/TopAuthors";

/**
 * Banner tap routing — mirrors BannerFor enum / doc/Banner-App-Navigation-Guide.md.
 * Used by desktop HomePage and mobile MobileHome (same destinations).
 */

export type BannerNavInput = {
  bannerFor: number;
  targetId?: string | null;
  slug?: string | null;
  name?: string | null;
};

export type BannerNavResult =
  | { kind: "none" }
  | { kind: "external"; url: string }
  | {
      kind: "navigate";
      path: string;
      state?: Record<string, unknown>;
      /** localStorage "type" for audio / pravachan / DailySatsang players */
      storageType?: "audios" | "pravachans" | "DailySatsang";
    };

const isHttpUrl = (value: string): boolean =>
  /^https?:\/\//i.test(value.trim());

const pathSeg = (slug: string, targetId: string): string =>
  encodeURIComponent(slug || targetId);

const hasTarget = (targetId: string): boolean => targetId.trim().length > 0;

/** BannerFor values that need a non-empty targetId (else fall back to product hub). */
const NEEDS_TARGET = new Set<number>([
  2, 3, 5, 6, 8, 9, 11, 12, 13, 14, 15, 20, 21, 22, 24, 25, 26, 27, 30, 32, 33,
  35, 37, 39, 40, 42, 44, 46, 47, 49, 51, 53, 54, 56, 57, 60, 61, 62, 63, 64, 100,
]);

const hubFor = (bannerFor: number): BannerNavResult => {
  switch (bannerFor) {
    case 2:
    case 3:
    case 13:
    case 24:
      return { kind: "navigate", path: "/books" };
    case 5:
    case 6:
    case 14:
    case 25:
      return { kind: "navigate", path: "/audios" };
    case 8:
    case 9:
    case 15:
    case 26:
      return { kind: "navigate", path: "/articles" };
    case 11:
      return { kind: "navigate", path: "/messages" };
    case 12:
      return { kind: "navigate", path: "/" };
    case 20:
    case 21:
    case 22:
    case 27:
      return { kind: "navigate", path: "/pravachans" };
    case 30:
    case 64:
      return { kind: "navigate", path: "/divinequotes" };
    case 32:
    case 33:
    case 35:
    case 37:
      return { kind: "navigate", path: "/magazines/kalyan" };
    case 39:
    case 40:
    case 42:
    case 44:
      return { kind: "navigate", path: "/magazines/kalyanakalpataru" };
    case 46:
    case 47:
    case 49:
    case 51:
      return { kind: "navigate", path: "/magazines/geetgovind" };
    case 53:
    case 54:
      return { kind: "navigate", path: "/magazines/vivekvani" };
    case 56:
    case 57:
    case 100:
      return { kind: "navigate", path: "/DailySatsang" };
    case 60:
    case 61:
    case 62:
      return { kind: "navigate", path: "/videos" };
    case 63:
      return { kind: "none" };
    default:
      return { kind: "none" };
  }
};

/**
 * Pure mapper: BannerFor + target → navigate / external / none.
 * Prefer slug in the URL path; put Mongo ids in location.state.
 */
export function resolveBannerNavigation(
  banner: BannerNavInput
): BannerNavResult {
  const bannerFor = Number(banner.bannerFor);
  const targetId = String(banner.targetId ?? "").trim();
  const slug = String(banner.slug ?? "").trim();
  const name = String(banner.name ?? "").trim();
  const seg = pathSeg(slug, targetId);

  if (bannerFor === 0 || Number.isNaN(bannerFor)) {
    return { kind: "none" };
  }

  // Custom URL — API copies CustomUrl into targetId
  if (bannerFor === 63) {
    if (isHttpUrl(targetId)) {
      return { kind: "external", url: targetId };
    }
    return { kind: "none" };
  }

  if (NEEDS_TARGET.has(bannerFor) && !hasTarget(targetId) && !slug) {
    return hubFor(bannerFor);
  }

  switch (bannerFor) {
    // --- Books ---
    case 1:
    case 28:
      return { kind: "navigate", path: "/books" };
    case 2:
      return {
        kind: "navigate",
        path: `/books/${seg}`,
        state: { bookId: targetId, bookSlug: slug || targetId, bookName: name },
      };
    case 3:
      return {
        kind: "navigate",
        path: `/books/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };
    case 13:
      return {
        kind: "navigate",
        path: `/books/author/${seg}`,
        state: {
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };
    case 16:
      // Former /books/special list — use canonical books list
      return { kind: "navigate", path: "/books" };
    case 24:
      return {
        kind: "navigate",
        path: `/books/language/${seg}`,
        state: { langId: targetId },
      };

    // --- Audios ---
    case 4:
      return { kind: "navigate", path: "/audios" };
    case 5: {
      // Fix: use real slug/targetId — never hardcode a track
      if (!slug && !targetId) {
        return { kind: "navigate", path: "/audios" };
      }
      const audioSeg = pathSeg(slug, targetId);
      return {
        kind: "navigate",
        path: `/audios/${audioSeg}`,
        state: {
          audioId: targetId,
          audioslug: slug || targetId,
        },
        storageType: "audios",
      };
    }
    case 6:
      return {
        kind: "navigate",
        path: `/audios/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };
    case 14:
      return {
        kind: "navigate",
        path: `/audios/author/${seg}`,
        state: {
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };
    case 17:
      // Former /audios/special list — use canonical audios list
      return { kind: "navigate", path: "/audios" };
    case 25:
      return {
        kind: "navigate",
        path: `/audios/language/${seg}`,
        state: { langId: targetId },
      };

    // --- Articles ---
    case 7:
      return { kind: "navigate", path: "/articles" };
    case 8:
      return {
        kind: "navigate",
        path: `/articles/${seg}`,
        state: { articleId: targetId },
      };
    case 9:
      return {
        kind: "navigate",
        path: `/articles/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };
    case 15:
      return {
        kind: "navigate",
        path: `/articles/author/${seg}`,
        state: {
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };
    case 18:
      // Former /articles/special list — use canonical articles list
      return { kind: "navigate", path: "/articles" };
    case 26:
      return {
        kind: "navigate",
        path: `/articles/language/${seg}`,
        state: { langId: targetId },
      };

    // --- Messages ---
    case 10:
      return { kind: "navigate", path: "/messages" };
    case 11:
      return {
        kind: "navigate",
        path: `/messages/${seg}`,
        state: { messageId: targetId },
      };

    // --- Author profile ---
    case 12: {
      const authorSlug = resolveTopAuthorSlug(slug || targetId) || slug || targetId;
      return {
        kind: "navigate",
        path: `/author/${encodeURIComponent(authorSlug)}`,
        state: { authorId: targetId, authorSlug, authorName: name },
      };
    }

    // --- Pravachans ---
    case 19:
      return { kind: "navigate", path: "/pravachans" };
    case 20:
      return {
        kind: "navigate",
        path: `/pravachans/${seg}`,
        state: {
          audioId: targetId,
          audioslug: slug || targetId,
        },
        storageType: "pravachans",
      };
    case 21:
      return {
        kind: "navigate",
        path: `/pravachans/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };
    case 22:
      return {
        kind: "navigate",
        path: `/pravachans/author/${seg}`,
        state: {
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };
    case 23:
      // Former /pravachans/special list — use canonical pravachans list
      return { kind: "navigate", path: "/pravachans" };
    case 27:
      return {
        kind: "navigate",
        path: `/pravachans/language/${seg}`,
        state: { langId: targetId },
      };

    // --- Quotes (64 = notification quirk → same as Quotes_Single) ---
    case 29:
      return { kind: "navigate", path: "/divinequotes" };
    case 30:
    case 64:
      return {
        kind: "navigate",
        path: `/divinequotes/${encodeURIComponent(targetId || slug)}`,
        state: { quoteId: targetId },
      };

    // --- Kalyan ---
    case 31:
    case 34:
      return { kind: "navigate", path: "/magazines/kalyan" };
    case 32:
      return {
        kind: "navigate",
        path: `/magazines/kalyan/${seg}`,
        state: { bookId: targetId, bookSlug: slug || targetId, bookName: name },
      };
    case 33:
      return {
        kind: "navigate",
        path: `/magazines/kalyan/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };
    case 35:
      return {
        kind: "navigate",
        path: `/magazines/kalyan/author/${seg}`,
        state: {
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };
    case 36:
      // Former /magazines/kalyan/special list — use canonical kalyan list
      return { kind: "navigate", path: "/magazines/kalyan" };
    case 37:
      return {
        kind: "navigate",
        path: `/magazines/kalyan/language/${seg}`,
        state: { langId: targetId },
      };

    // --- Kalyan Kalpataru ---
    case 38:
    case 41:
      return { kind: "navigate", path: "/magazines/kalyanakalpataru" };
    case 39:
      return {
        kind: "navigate",
        path: `/magazines/kalyanakalpataru/${seg}`,
        state: { bookId: targetId, bookSlug: slug || targetId, bookName: name },
      };
    case 40:
      return {
        kind: "navigate",
        path: `/magazines/kalyanakalpataru/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };
    case 42:
      return {
        kind: "navigate",
        path: `/magazines/kalyanakalpataru/author/${seg}`,
        state: {
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };
    case 43:
      // Former /magazines/kalyanakalpataru/special list — use canonical kalpataru list
      return { kind: "navigate", path: "/magazines/kalyanakalpataru" };
    case 44:
      return {
        kind: "navigate",
        path: `/magazines/kalyanakalpataru/language/${seg}`,
        state: { langId: targetId },
      };

    // --- Geet Govind (monthly magazines) ---
    case 45:
    case 48:
      return { kind: "navigate", path: "/magazines/geetgovind" };
    case 46:
      return {
        kind: "navigate",
        path: `/magazines/geetgovind/${seg}`,
        state: { bookId: targetId, bookSlug: slug || targetId, bookName: name },
      };
    case 47:
      return {
        kind: "navigate",
        path: `/magazines/geetgovind/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };
    case 49:
      return {
        kind: "navigate",
        path: `/magazines/geetgovind/author/${seg}`,
        state: {
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };
    case 50:
      // Former /magazines/geetgovind/special list — use canonical geetgovind list
      return { kind: "navigate", path: "/magazines/geetgovind" };
    case 51:
      return {
        kind: "navigate",
        path: `/magazines/geetgovind/language/${seg}`,
        state: { langId: targetId },
      };

    // --- Vivek Vani ---
    case 52:
      return { kind: "navigate", path: "/magazines/vivekvani" };
    case 53:
      return {
        kind: "navigate",
        path: `/magazines/vivekvani/${seg}`,
        state: { bookId: targetId, bookSlug: slug || targetId, bookName: name },
      };
    case 54:
      return {
        kind: "navigate",
        path: `/magazines/vivekvani/category/${seg}`,
        state: { catId: targetId, catSlug: slug || targetId, catName: name },
      };

    // --- Audio podcast / Daily Satsang ---
    case 55:
    case 58:
      return { kind: "navigate", path: "/DailySatsang" };
    case 56:
    case 100: {
      if (!slug && !targetId) {
        return { kind: "navigate", path: "/DailySatsang" };
      }
      return {
        kind: "navigate",
        path: `/DailySatsang/${pathSeg(slug, targetId)}`,
        state: {
          audioId: targetId,
          audioslug: slug || targetId,
        },
        storageType: "DailySatsang",
      };
    }
    case 57:
      return {
        kind: "navigate",
        path: "/DailySatsang",
        state: {
          type: "DailySatsang",
          authorId: targetId,
          authorSlug: slug || targetId,
          authorName: name,
        },
      };

    // --- Videos ---
    case 59:
      return { kind: "navigate", path: "/videos" };
    case 60:
      return {
        kind: "navigate",
        path: "/videos",
        state: { videoId: targetId },
      };
    case 61:
      return {
        kind: "navigate",
        path: "/videos",
        state: { categoryId: targetId },
      };
    case 62:
      return {
        kind: "navigate",
        path: "/videos",
        state: { authorId: targetId },
      };

    default: {
      // Unknown bannerFor: open URL if target looks like one, else stay
      if (isHttpUrl(targetId)) {
        return { kind: "external", url: targetId };
      }
      return { kind: "none" };
    }
  }
}

/**
 * Apply resolved banner navigation (shared by desktop + mobile home).
 * Pools logdetails and writes with console.log at end of the block.
 */
export function applyBannerNavigation(
  navigate: NavigateFunction,
  banner: BannerNavInput
): void {
  const logdetails: string[] = [];
  const bannerFor = Number(banner.bannerFor);
  const targetId = String(banner.targetId ?? "");
  const slug = String(banner.slug ?? "");

  logdetails.push(`bannerFor=${bannerFor}`);
  logdetails.push(`targetId=${targetId || "none"}`);
  logdetails.push(`slug=${slug || "none"}`);

  const result = resolveBannerNavigation(banner);
  logdetails.push(`kind=${result.kind}`);

  if (result.kind === "none") {
    logdetails.push("action=stay_home");
    console.log("applyBannerNavigation ->", logdetails.join(" | "));
    return;
  }

  if (result.kind === "external") {
    logdetails.push(`url=${result.url}`);
    logdetails.push("action=window_open");
    console.log("applyBannerNavigation ->", logdetails.join(" | "));
    window.open(result.url, "_blank", "noopener,noreferrer");
    return;
  }

  if (result.storageType) {
    localStorage.setItem("type", result.storageType);
    logdetails.push(`storageType=${result.storageType}`);
  }

  logdetails.push(`path=${result.path}`);
  if (result.state) {
    logdetails.push(`state_keys=${Object.keys(result.state).join(",")}`);
  }
  logdetails.push("action=navigate");
  console.log("applyBannerNavigation ->", logdetails.join(" | "));

  navigate(result.path, result.state ? { state: result.state } : undefined);
}
